import { z } from "zod";
import type { Operation } from "../../types";
import { crmIdParams, smartViewQuerySchema } from "./_shared";

export { smartViewQuerySchema };

const smartViewRowSchema = z.object({
  id: z.number(),
  workspace_id: z.number(),
  slug: z.string(),
  name: z.string(),
  entity_kind: z.enum(["crm_lead", "crm_opportunity"]),
  query_json: z.string(),
  shared: z.number(),
  sort_key: z.string(),
  owner_actor_id: z.number().nullable(),
});

const list: Operation<
  void,
  { entity_kind?: "crm_lead" | "crm_opportunity" },
  void,
  { smart_views: z.infer<typeof smartViewRowSchema>[] }
> = {
  method: "GET",
  path: () => "/api/crm/smart_views",
  query: z
    .object({
      entity_kind: z.enum(["crm_lead", "crm_opportunity"]).optional(),
    })
    .partial(),
  response: z.object({ smart_views: z.array(smartViewRowSchema) }),
};

const create: Operation<
  void,
  void,
  {
    slug: string;
    name: string;
    entity_kind: "crm_lead" | "crm_opportunity";
    query: z.infer<typeof smartViewQuerySchema>;
    shared?: boolean;
  },
  z.infer<typeof smartViewRowSchema>
> = {
  method: "POST",
  path: () => "/api/crm/smart_views",
  body: z.object({
    slug: z.string().min(1),
    name: z.string().min(1),
    entity_kind: z.enum(["crm_lead", "crm_opportunity"]),
    query: smartViewQuerySchema,
    shared: z.boolean().optional(),
  }),
  response: smartViewRowSchema,
  // inserts into the smart_views collection (entity_kind filters wild-carded).
  invalidates: [list],
};

const update: Operation<
  { id: number },
  void,
  {
    name?: string;
    query?: z.infer<typeof smartViewQuerySchema>;
    shared?: boolean;
  },
  z.infer<typeof smartViewRowSchema>
> = {
  method: "PATCH",
  path: ({ id }) => `/api/crm/smart_views/${id}`,
  params: crmIdParams,
  body: z.object({
    name: z.string().min(1).optional(),
    query: smartViewQuerySchema.optional(),
    shared: z.boolean().optional(),
  }),
  response: smartViewRowSchema,
  invalidates: [list],
};

const remove: Operation<
  { id: number },
  void,
  void,
  { ok: true }
> = {
  method: "DELETE",
  path: ({ id }) => `/api/crm/smart_views/${id}`,
  params: crmIdParams,
  response: z.object({ ok: z.literal(true) }),
  invalidates: [list],
};

export const smart_views = { list, create, update, delete: remove };
